import { useCallback, useMemo } from "react";
import { useListContext, useRecordContext } from "react-admin";
import { Card, CardContent, Typography, Box, alpha } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import MarkChatReadOutlinedIcon from "@mui/icons-material/MarkChatReadOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

export type RecipientStatusKey = "ALL" | "PENDING" | "SENT" | "DELIVERED" | "READ" | "FAILED";

export type StatusTileColor = "primary" | "info" | "success" | "warning" | "error" | "secondary";

export interface RecipientStatusTileConfig {
  key: RecipientStatusKey;
  statusValue?: string;
  label: string;
  icon: SvgIconComponent;
  color: StatusTileColor;
}

export const RECIPIENT_STATUS_TILES: RecipientStatusTileConfig[] = [
  { key: "ALL", label: "All", icon: PeopleAltOutlinedIcon, color: "primary" },
  {
    key: "PENDING",
    statusValue: "PENDING",
    label: "Pending",
    icon: HourglassEmptyOutlinedIcon,
    color: "warning",
  },
  { key: "SENT", statusValue: "SENT", label: "Sent", icon: SendOutlinedIcon, color: "info" },
  {
    key: "DELIVERED",
    statusValue: "DELIVERED",
    label: "Delivered",
    icon: DoneAllOutlinedIcon,
    color: "success",
  },
  {
    key: "READ",
    statusValue: "READ",
    label: "Read",
    icon: MarkChatReadOutlinedIcon,
    color: "secondary",
  },
  {
    key: "FAILED",
    statusValue: "FAILED",
    label: "Failed",
    icon: ErrorOutlineOutlinedIcon,
    color: "error",
  },
];

export const RECIPIENT_STATUS_TABS = RECIPIENT_STATUS_TILES;

export function extractRecipientCounts(
  metaCounts: unknown,
  storageCountsRaw: string | null,
  broadcastRecord?: Record<string, unknown> | null
): Record<RecipientStatusKey, number> {
  const counts: Record<RecipientStatusKey, number> = {
    ALL: 0,
    PENDING: 0,
    SENT: 0,
    DELIVERED: 0,
    READ: 0,
    FAILED: 0,
  };

  let resolvedMeta: Record<string, number> | null = null;

  if (metaCounts && typeof metaCounts === "object") {
    if (Array.isArray(metaCounts)) {
      resolvedMeta = {};
      for (const item of metaCounts as Array<{ status?: string; count?: number }>) {
        if (item.status) {
          resolvedMeta[item.status.toUpperCase()] = Number(item.count ?? 0);
        }
      }
    } else {
      resolvedMeta = metaCounts as Record<string, number>;
    }
  }

  if (!resolvedMeta && storageCountsRaw) {
    try {
      const parsed = JSON.parse(storageCountsRaw);
      if (parsed && typeof parsed === "object") {
        if (Array.isArray(parsed)) {
          resolvedMeta = {};
          for (const item of parsed as Array<{ status?: string; count?: number }>) {
            if (item.status) {
              resolvedMeta[item.status.toUpperCase()] = Number(item.count ?? 0);
            }
          }
        } else {
          resolvedMeta = parsed as Record<string, number>;
        }
      }
    } catch {
      resolvedMeta = null;
    }
  }

  if (resolvedMeta && typeof resolvedMeta === "object") {
    counts.PENDING = Number(resolvedMeta.PENDING ?? 0);
    counts.SENT = Number(resolvedMeta.SENT ?? 0);
    counts.DELIVERED = Number(resolvedMeta.DELIVERED ?? 0);
    counts.READ = Number(resolvedMeta.READ ?? 0);
    counts.FAILED = Number(resolvedMeta.FAILED ?? 0);
    const sum = counts.PENDING + counts.SENT + counts.DELIVERED + counts.READ + counts.FAILED;
    counts.ALL =
      resolvedMeta.TOTAL !== undefined && resolvedMeta.TOTAL !== null
        ? Math.max(Number(resolvedMeta.TOTAL), sum)
        : sum;
    return counts;
  }

  if (broadcastRecord) {
    const total = Number(broadcastRecord.totalRecipients ?? 0);
    const failure = Number(broadcastRecord.failureCount ?? 0);
    const success = Number(broadcastRecord.successCount ?? 0);
    counts.ALL = Number.isFinite(total) ? total : 0;
    counts.FAILED = Number.isFinite(failure) ? failure : 0;
    counts.SENT = Number.isFinite(success) ? success : 0;
    counts.PENDING = Math.max(0, counts.ALL - counts.FAILED - counts.SENT);
  }

  return counts;
}

export function resolveNextStatusFilter(
  currentFilters: Record<string, unknown> | undefined,
  tileStatusValue?: string
): Record<string, unknown> {
  const nextFilters = { ...(currentFilters ?? {}) };
  const currentStatus = (currentFilters?.status as string | undefined)?.toUpperCase();

  if (!tileStatusValue || currentStatus === tileStatusValue.toUpperCase()) {
    delete nextFilters.status;
  } else {
    nextFilters.status = tileStatusValue;
  }
  return nextFilters;
}

export interface RecipientStatusTilesProps {
  broadcastRecord?: Record<string, unknown> | null;
}

export function RecipientStatusTiles({
  broadcastRecord: propRecord,
}: RecipientStatusTilesProps = {}) {
  const listContext = useListContext();
  const contextRecord = useRecordContext();
  const broadcastRecord = propRecord ?? contextRecord;
  const { filterValues, setFilters, meta, resource } = listContext;

  const broadcastId =
    (broadcastRecord?.id as string | undefined) ??
    (filterValues?.broadcastId as string | undefined);

  const rawStorage =
    typeof window !== "undefined" && broadcastId
      ? localStorage.getItem(`${resource}_${broadcastId}_count`)
      : null;

  const metaCounts = (meta as { counts?: unknown } | undefined)?.counts;

  const counts = useMemo(
    () => extractRecipientCounts(metaCounts, rawStorage, broadcastRecord),
    [metaCounts, rawStorage, broadcastRecord]
  );

  const activeStatus = (filterValues?.status as string | undefined)?.toUpperCase() || "ALL";

  const handleTileClick = useCallback(
    (statusValue?: string) => {
      const nextFilters = resolveNextStatusFilter(filterValues, statusValue);
      setFilters(nextFilters, undefined, false);
    },
    [filterValues, setFilters]
  );

  return (
    <Box
      data-testid="recipient-status-tiles"
      sx={{
        width: "100%",
        px: { xs: 1.5, sm: 2, md: 3 },
        pt: { xs: 1.5, sm: 2 },
        pb: 1.5,
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(6, 1fr)",
        },
        gap: { xs: 1, sm: 1.5, md: 2 },
      }}
    >
      {RECIPIENT_STATUS_TILES.map((tile) => {
        const isSelected =
          tile.key === "ALL"
            ? !filterValues?.status || activeStatus === "ALL"
            : activeStatus === tile.key;
        const count = counts[tile.key] ?? 0;
        const Icon = tile.icon;
        const colorKey = tile.color;

        return (
          <Card
            key={tile.key}
            variant="outlined"
            onClick={() => handleTileClick(tile.statusValue)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleTileClick(tile.statusValue);
              }
            }}
            tabIndex={0}
            role="button"
            aria-pressed={isSelected}
            data-testid={`status-tile-${tile.key.toLowerCase()}`}
            sx={{
              cursor: "pointer",
              borderRadius: 1.5,
              borderWidth: isSelected ? 2 : 1,
              borderColor: (theme) =>
                isSelected
                  ? theme.palette[colorKey]?.main || theme.palette.primary.main
                  : theme.palette.divider,
              bgcolor: (theme) =>
                isSelected
                  ? alpha(theme.palette[colorKey]?.main || theme.palette.primary.main, 0.08)
                  : theme.palette.background.paper,
              "&:hover": {
                bgcolor: (theme) =>
                  isSelected
                    ? alpha(theme.palette[colorKey]?.main || theme.palette.primary.main, 0.12)
                    : alpha(theme.palette[colorKey]?.main || theme.palette.primary.main, 0.04),
              },
              "&:focus-visible": {
                outline: (theme) =>
                  `2px solid ${theme.palette[colorKey]?.main || theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <CardContent
              sx={{
                p: { xs: 1.25, sm: 1.5, md: 1.75 },
                "&:last-child": { pb: { xs: 1.25, sm: 1.5, md: 1.75 } },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: { xs: 0.5, sm: 1 },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {tile.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 0.25,
                      fontWeight: 700,
                      color: (theme) => theme.palette[colorKey]?.main || theme.palette.text.primary,
                      fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.3rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    {count.toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette[colorKey]?.light || theme.palette.primary.light,
                    borderRadius: "50%",
                    p: { xs: 0.75, sm: 1 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: { xs: 18, sm: 20, md: 22 },
                      color: "common.white",
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}

export default RecipientStatusTiles;
